import { format, isValid, parseISO } from 'date-fns'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useListRenewals } from '@/utils/api/membership-service'
import type {
  ListRenewalsParams,
  MembershipRenewalResponseDTO,
  PageMembershipRenewalResponseDTO,
} from '@/utils/api/membership-service/models'

import { useMemberRenewalStatus } from './hooks/useMemberRenewalStatus'

const DATE_FORMAT = 'PPpp'
const DEFAULT_PARAMS: ListRenewalsParams = { page: 0, size: 50 }
const currencyFormatter = new Intl.NumberFormat('en-UG', {
  style: 'currency',
  currency: 'UGX',
  maximumFractionDigits: 0,
})

function normaliseDate(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = parseISO(trimmed)
  if (isValid(parsed)) {
    return format(parsed, DATE_FORMAT)
  }

  const fallback = new Date(trimmed)
  if (!Number.isNaN(fallback.getTime())) {
    return format(fallback, DATE_FORMAT)
  }

  return null
}

function formatTermMonths(termMonths: number): string {
  if (!Number.isFinite(termMonths) || termMonths <= 0) {
    return '—'
  }

  if (termMonths % 12 === 0) {
    const years = termMonths / 12
    return `${years} year${years === 1 ? '' : 's'}`
  }

  return `${termMonths} month${termMonths === 1 ? '' : 's'}`
}

function formatAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) {
    return '—'
  }

  return currencyFormatter.format(amount)
}

function RenewalHistorySkeleton() {
  return (
    <Card className="border-muted">
      <CardHeader>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={`renewal-skeleton-${index}`} className="h-10 w-full" />
        ))}
      </CardContent>
    </Card>
  )
}

export function RenewalHistoryPage() {
  const renewalStatus = useMemberRenewalStatus()

  const applicationId = renewalStatus.latestApplication?.id ?? 0
  const renewalsQuery = useListRenewals<PageMembershipRenewalResponseDTO>(applicationId, DEFAULT_PARAMS, {
    query: {
      enabled: Boolean(renewalStatus.latestApplication?.id),
      select: (response) => response.data,
    },
  })

  const renewals = Array.isArray(renewalsQuery.data?.content)
    ? (renewalsQuery.data?.content as MembershipRenewalResponseDTO[])
    : []

  if (renewalStatus.isLoading || renewalsQuery.isPending) {
    return <RenewalHistorySkeleton />
  }

  if (!renewalStatus.latestApplication) {
    return (
      <Alert>
        <AlertTitle>No membership record found</AlertTitle>
        <AlertDescription>
          Submit a membership application first to see your renewal history.
        </AlertDescription>
      </Alert>
    )
  }

  if (renewalsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Unable to load renewals</AlertTitle>
        <AlertDescription>
          {renewalsQuery.error instanceof Error
            ? renewalsQuery.error.message
            : 'We could not retrieve your renewal history. Please try again later.'}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Membership renewal history</CardTitle>
        <CardDescription>
          Track all renewal submissions linked to your membership account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {renewals.length === 0 ? (
          <Alert>
            <AlertTitle>No renewals yet</AlertTitle>
            <AlertDescription>
              When you submit a renewal request it will appear here with its status and receipt.
            </AlertDescription>
          </Alert>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Submitted on</TableHead>
                <TableHead>Previous expiry</TableHead>
                <TableHead>New expiry</TableHead>
                <TableHead>Term</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renewals.map((renewal, index) => {
                const submittedOn = normaliseDate(renewal.renewedAt)
                return (
                  <TableRow
                    key={
                      renewal.id ??
                      `${submittedOn ?? 'renewal-row'}-${index}`
                    }
                  >
                    <TableCell>{submittedOn ?? '—'}</TableCell>
                    <TableCell>
                      {normaliseDate(renewal.previousExpiryAt) ?? '—'}
                    </TableCell>
                    <TableCell>{normaliseDate(renewal.newExpiryAt) ?? '—'}</TableCell>
                    <TableCell>
                      {typeof renewal.termMonths === 'number'
                        ? formatTermMonths(renewal.termMonths)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {typeof renewal.amount === 'number'
                        ? formatAmount(renewal.amount)
                        : '—'}
                    </TableCell>
                    <TableCell>{renewal.paymentReference ?? '—'}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
            <TableCaption>
              {renewals.length === 1
                ? 'Showing your most recent renewal request.'
                : `Showing ${renewals.length} renewal records.`}
            </TableCaption>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
